import type { Dispatch, RefObject, SetStateAction, SyntheticEvent } from 'react'

import { useCallback, useEffect, useRef } from 'react'
import { ScrollBehavior } from 'containers/RowContainer/RowContainer.h'
import { debounce } from 'utils/helpers'

interface IUseHintsScrollHandlersProps {
  ref: RefObject<HTMLDivElement | null>
  setRowScrollPosition: Dispatch<SetStateAction<number>>
}

const DELAY_SET_SCROLL_POSITION = 150

export const useHintsScrollHandlers = ({
  ref,
  setRowScrollPosition,
}: IUseHintsScrollHandlersProps) => {
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  const scrollTo = useCallback(
    ({ forward = false }: { forward?: boolean }): void => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
      timeoutRef.current = setTimeout(() => {
        const container = ref.current
        if (!container) return

        const scrollAmount = container.clientWidth
        const currentScrollPosition = container.scrollLeft
        const maxScrollLeft = container.scrollWidth - container.clientWidth

        const scrollBackPosition = Math.max(
          0,
          currentScrollPosition - scrollAmount
        )
        const scrollForwardPosition = Math.min(
          maxScrollLeft,
          currentScrollPosition + scrollAmount
        )
        const scrollToPosition = forward
          ? scrollForwardPosition
          : scrollBackPosition

        container.scrollTo({
          left: scrollToPosition,
          behavior: ScrollBehavior.smooth,
        })
      }, 10)
    },
    [ref]
  )

  const handleScroll = useCallback(
    debounce((_e: SyntheticEvent) => {
      if (ref.current) {
        setRowScrollPosition(() => ref.current?.scrollLeft ?? 0)
      }
    }, DELAY_SET_SCROLL_POSITION),
    [ref, setRowScrollPosition]
  )

  const next = useCallback(() => scrollTo({ forward: true }), [scrollTo])
  const prev = useCallback(() => scrollTo({ forward: false }), [scrollTo])

  return {
    next,
    prev,
    handleScroll,
  }
}
